import { useEffect, useState } from "react";
import { cx } from "../cx";
import { Icon } from "../Icon";
import { Popover, useDisclosure } from "../Popover";
import styles from "./ColorPicker.module.css";

/**
 * Clockify's current default project picker, in its display order (7, 1..6, 8, 9).
 * `value` is what gets stored on the project (the light-theme hex); `token` paints the swatch
 * so it follows the theme's variant.
 */
export const PROJECT_COLORS = [
  { token: "--default-picker-color-7", value: "#689F38" },
  { token: "--default-picker-color-1", value: "#E91E63" },
  { token: "--default-picker-color-2", value: "#AB47BC" },
  { token: "--default-picker-color-3", value: "#5C6BC0" },
  { token: "--default-picker-color-4", value: "#1976D2" },
  { token: "--default-picker-color-5", value: "#039BE5" },
  { token: "--default-picker-color-6", value: "#009688" },
  { token: "--default-picker-color-8", value: "#FF5722" },
  { token: "--default-picker-color-9", value: "#8D6E63" },
] as const;

const HEX = /^#[0-9a-f]{6}$/i;

export function isHexColor(value: string): boolean {
  return HEX.test(value);
}

/** Swatch paint for a stored colour: the theme token for palette colours, the raw hex for custom ones. */
export function colorPaint(value: string): string {
  const preset = PROJECT_COLORS.find((c) => c.value.toLowerCase() === value.toLowerCase());
  return preset ? `var(${preset.token})` : value;
}

export interface ColorPickerProps {
  value: string;
  onChange: (hex: string) => void;
  /** Hides the custom hex input. */
  presetsOnly?: boolean;
  disabled?: boolean;
  "aria-label"?: string;
  className?: string;
}

export function ColorPicker({
  value,
  onChange,
  presetsOnly,
  disabled,
  className,
  "aria-label": ariaLabel = "Pick a color",
}: ColorPickerProps) {
  const { open, toggle, close, anchorRef } = useDisclosure<HTMLButtonElement>();
  const [custom, setCustom] = useState(value);
  useEffect(() => setCustom(value), [value]);
  const customValid = isHexColor(custom);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={cx(styles.trigger, className)}
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggle}
      >
        <span className={styles.triggerSwatch} style={{ background: colorPaint(value) }} />
        <Icon name="chevron-down" size={14} />
      </button>
      <Popover
        open={open}
        onClose={close}
        anchorRef={anchorRef}
        placement="bottom-start"
        className={styles.panel}
        role="dialog"
        aria-label={ariaLabel}
      >
        <div className={styles.grid} role="listbox" aria-label={ariaLabel}>
          {PROJECT_COLORS.map((c) => {
            const selected = c.value.toLowerCase() === value.toLowerCase();
            return (
              <button
                key={c.value}
                type="button"
                role="option"
                aria-selected={selected}
                aria-label={c.value}
                className={styles.swatch}
                style={{ background: `var(${c.token})` }}
                onClick={() => {
                  onChange(c.value);
                  close();
                }}
              >
                {selected && <Icon name="check" size={14} strokeWidth={2.5} className={styles.check} />}
              </button>
            );
          })}
        </div>
        {!presetsOnly && (
          <form
            className={styles.custom}
            onSubmit={(e) => {
              e.preventDefault();
              if (!customValid) return;
              onChange(custom.toUpperCase());
              close();
            }}
          >
            <span
              className={styles.customSwatch}
              style={{ background: customValid ? custom : undefined }}
              aria-hidden="true"
            />
            <input
              className={cx(styles.customInput, !customValid && styles.invalid)}
              value={custom}
              maxLength={7}
              aria-label="Custom color (hex)"
              aria-invalid={!customValid || undefined}
              onChange={(e) => {
                const v = e.target.value.trim();
                setCustom(v.startsWith("#") ? v : `#${v}`);
              }}
            />
            <button type="submit" className={styles.apply} disabled={!customValid}>
              OK
            </button>
          </form>
        )}
      </Popover>
    </>
  );
}
